import { computed, makeObservable, observable, runInAction } from 'mobx';
import {
   CandidateCv,
   CandidateDeleteCvDto,
   CandidateDeleteCvResponse,
   GetCvListResponse,
   LoadCvPdfDto,
   LoadCvPdfResponse,
} from './types';
import { AsyncState } from 'shared/libs/state';
import { PaginationControl } from 'shared/components/pagination';
import { ManageCvsApiService } from './ManageCvsApiService';
export class ManageCvsStore {
   readonly loadCvsState = new AsyncState<GetCvListResponse>();
   readonly deleteCvState = new AsyncState<CandidateDeleteCvResponse>();
   readonly loadCvPdfState = new AsyncState<LoadCvPdfResponse>();

   readonly pagination = new PaginationControl();
   private readonly PER_PAGE = 10;
   cvId: number | null = null;

   rawCvs: CandidateCv[] = [];
   constructor() {
      makeObservable(this, {
         rawCvs: observable,
         cvId: observable,
         alerts: computed,
         totalPages: computed,
         canNext: computed,
         canPrev: computed,
         recordsCount: computed,
      });
   }

   get totalPages(): number {
      return Math.ceil(this.rawCvs.length / this.PER_PAGE);
   }

   get alerts(): CandidateCv[] {
      const firstIdx = this.pagination.page * this.PER_PAGE;
      return this.rawCvs.slice(firstIdx, firstIdx + this.PER_PAGE);
   }

   get canPrev(): boolean {
      return this.pagination.page > 0;
   }

   get canNext(): boolean {
      const next = this.pagination.page + 1;
      return next < this.totalPages;
   }

   get recordsCount(): string {
      const firstRecord = this.pagination.page * this.PER_PAGE + 1;
      let lastRecord = firstRecord + this.PER_PAGE - 1;
      if (lastRecord > this.rawCvs.length) {
         lastRecord = this.rawCvs.length;
      }
      return `${firstRecord} - ${lastRecord}`;
   }

   onNext(): void {
      if (this.canNext) {
         this.pagination.setPage(this.pagination.page + 1);
      }
   }

   onPrev(): void {
      if (this.canPrev) {
         this.pagination.setPage(this.pagination.page - 1);
      }
   }

   async loadCvs(): Promise<void> {
      await this.loadCvsState.run(() => ManageCvsApiService.loadCvs(null, {}), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               runInAction(() => {
                  this.rawCvs = resp.response;
               });
            }
         },
      });
   }

   async deleteCv({
      dto,
      onSuccess,
      onError,
   }: {
      dto: CandidateDeleteCvDto;
      onSuccess?: (res: CandidateDeleteCvResponse) => void;
      onError?: (e: string) => void;
   }) {
      await this.deleteCvState.run(() => ManageCvsApiService.deleteCv(dto), {
         onSuccess: (resp) => {
            if (resp.status === 200) {
               onSuccess?.(resp);
               this.loadCvs();
            } else {
               onError?.(resp.message);
            }
         },
         onError: (err) => {
            onError?.(err.message);
         },
      });
   }

   async loadCvPdf({
      dto,
      onSuccess,
      onError,
   }: {
      dto: LoadCvPdfDto;
      onSuccess?: (res: LoadCvPdfResponse) => void;
      onError?: (e: string) => void;
   }): Promise<void> {
      runInAction(() => {
         this.cvId = dto.id;
      });

      await this.loadCvPdfState.run(() => ManageCvsApiService.loadCvPdf(dto), {
         onSuccess: (resp) => {
            if (resp) {
               onSuccess?.(resp);
               this.loadCvs();
            } else {
               onError?.(resp);
            }
         },
         onError: (err) => {
            onError?.(err.message);
         },
         onFinally: () => {
            runInAction(() => {
               this.cvId = null;
            });
         },
      });
   }
}
